<template>
  <div>
    <section
      v-if="hasSomeButtonToShow || hasErrorButton"
      class="services-section"
    >
      <Heading :level="3" class="trade-position-heading__title">
        Serviços
      </Heading>
      <ErrorHandler
        v-if="hasErrorButton"
        :loading="isLoadingError"
        @retry="emits('retry-services')"
      />

      <div class="services-section__list">
        <ButtonBoxLaunch
          v-for="(button, index) in props.serviceButtons"
          :key="`${button.name || 'err'}-${index}`"
          :disabled="!button.enabled"
          :label="button.label"
          :icon="button.icon"
          :badge-label="button.chipLabel"
          :loading="button.name === 'error' && props.isLoadingError"
          :class="[
            getChipCustomStyle(button.chipStyle),
            {
              'services-section__button--error': button.name === 'error',
              'services-section__button--loading':
                button.name === 'error' && props.isLoadingError
            }
          ]"
          @click="
            button.name === 'error'
              ? props.isLoadingError
                ? null
                : emits('retry-services')
              : handleClick(button)
          "
        />
      </div>
    </section>
    <RequestBrokerModal
      :is-open="showHomeBrokerModal"
      :broker-status="homeBrokerStatus"
      @close="showHomeBrokerModal = false"
      @update-status="updateHomeBrokerStatus"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import { ButtonBoxLaunch, Heading } from '@warrenbrasil/nebraska-web';
import { RequestBrokerModal } from './components';

import { TradeButton } from '@/modules/trade/services/buttons';
import { TradeHomeBrokerStatus } from '@/modules/trade/services/account';
import ErrorHandler from '../GeneralSidebarList/components/ErrorHandler/ErrorHandler.vue';

interface Emits {
  (event: 'open-home-broker'): void;
  (event: 'open-copytrade'): void;
  (event: 'retry-services'): void; // Novo emit
}

const props = defineProps<{
  serviceButtons: TradeButton[];
  isLoadingError?: boolean; // Nova prop vindo do pai
}>();

const emits = defineEmits<Emits>();

const router = useRouter();

const showHomeBrokerModal = ref(false);
const homeBrokerStatus = ref('');

const isButtonVisible = (button: TradeButton) => {
  // Confiamos plenamente na lista enviada pelo pai
  if (button.name === 'error') return true;
  if (button.name === 'marginAllocation') return true;
  return button.enabled;
};

const hasSomeButtonToShow = computed(() =>
  props.serviceButtons?.some(
    (button: TradeButton) => button.enabled && button.name !== 'error'
  )
);

const hasErrorButton = computed(() =>
  props.serviceButtons?.some((button: TradeButton) => button.name === 'error')
);

function getChipCustomStyle(chipStyle: string | undefined) {
  if (!chipStyle) return;
  return `services-section__chip--${chipStyle}`;
}

function handleClick(button: TradeButton) {
  switch (button.name) {
    case 'securitiesLendingOverview':
      router.push({ name: button.name, query: { group: 'btcPosition' } });
      break;

    case 'homeBroker':
      handleHomeBrokerClick(button.status as string);
      break;

    case 'copytrade':
      handleCopytradeClick();
      break;

    default:
      router.push({ name: button.name });
      break;
  }
}

function handleHomeBrokerClick(status: string) {
  if (status === TradeHomeBrokerStatus.Enabled) {
    emits('open-home-broker');
    return;
  }

  showHomeBrokerModal.value = true;
  homeBrokerStatus.value = status;
}

function handleCopytradeClick() {
  emits('open-copytrade');
}

async function updateHomeBrokerStatus() {
  showHomeBrokerModal.value = false;
}
</script>

<style lang="less" scoped>
.services-section {
  margin-bottom: @size-spacing-x800;

  /* Estado de Carregamento para o Card de Erro Individual */
  &__button--loading {
    pointer-events: none; /* Bloqueia cliques extras durante o loading */

    :deep(.nebraska-button-box-launch) {
      opacity: 0.5 !important;
      cursor: wait !important;
    }
  }

  &__button--error {
    cursor: pointer !important;

    // Alvo no container principal do botão
    :deep(.nebraska-button-box-launch) {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      text-align: center !important;

      // Cor de fundo e borda solicitadas
      background-color: var(--primary-background) !important;
      border-color: @status-alert-background !important;
    }

    // Alvo na Label (Texto)
    :deep(.button-box-launch__label) {
      display: block !important;
      width: 100% !important;
      color: var(--element-primary) !important;
      margin-top: @size-spacing-x150 !important;
      text-align: center !important;

      -webkit-text-fill-color: var(--element-primary) !important;
      opacity: 1 !important;
    }

    // Alvo no Ícone
    :deep(.button-box-launch__icon),
    :deep(.icon) {
      color: @status-alert-background !important;
      margin-bottom: 0 !important;
      display: flex !important;
      justify-content: center !important;
    }
  }

  &__list {
    margin-top: @size-spacing-x400;
    display: flex;
    gap: @size-spacing-x400;
    flex-flow: row wrap;

    :deep(.nebraska-button-box-launch) {
      min-width: 162px;
    }
  }
}
</style>
